import React, { useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { MyCompanyAdminReportsGrid } from 'Deer/pages/Company/DisciplinaryActions/MyCompanyAdminReportsGrid';
import { BulkActionContextProvider } from 'Deer/pages/Company/DisciplinaryActions/components/BulkActionHeader/BulkActionContext';
import { useGetAdminReports } from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { resetReviewManageSidepanelState } from 'reducers/reviewManageSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { MODULE_KEYS } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { DisciplinaryActionEmployeeTrendChart } from './components/AdminReportGraphs/DisciplinaryActionEmployeeTrendChart';
import { DisciplinaryActionViolationCountChart } from './components/AdminReportGraphs/DisciplinaryActionViolationCountChart';
import { AdminReportMetricCard } from './components/AdminReportMetricCard';
import {
  useSelectDisciplinaryActionsReportDateRange,
  useSelectDisciplinaryActionsSelectedCompanies,
  useSelectDisciplinaryActionsSelectedIntent,
  useSelectDisciplinaryActionsSelectedLocations,
} from './slice/DisciplinaryActionsIntentSlice';

function MyCompanyAllAdminReportsIntent({ gridRef }) {
  const dispatch = useDispatch();
  const cohortDetails = useSelectedView();
  const { id } = useSelectDisciplinaryActionsSelectedIntent() || {};
  const reportDateRange = useSelectDisciplinaryActionsReportDateRange();

  // convert to YYYY-MM-DD
  const formattedReportDateRange = {
    startDate: format(reportDateRange.startDate, 'yyyy-MM-dd'),
    endDate: format(reportDateRange.endDate, 'yyyy-MM-dd'),
  };

  const selectedCompanies = useSelectDisciplinaryActionsSelectedCompanies();
  const selectedLocations = useSelectDisciplinaryActionsSelectedLocations();

  /* Grid State persistence Key */
  const viewId = cohortDetails?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const uniqueId = 'MyCompanyAllAdminReportsGrid';

    return getPersistenceKey({
      moduleName: MODULE_KEYS.DISCIPLINARY_ACTIONS,
      viewId,
      tabId: '',
      uniqueId,
    });
  }, [viewId]);

  const {
    data: adminReportsData,
    isLoading,
    error,
  } = useGetAdminReports({
    cohortId: cohortDetails?.id,
    rowGroupCols: [
      {
        id: 'EMPLOYEE',
        displayName: 'Employee',
        field: 'EMPLOYEE',
      },
    ],
    baseFilter: formattedReportDateRange,
  });

  useEffect(
    () => () => {
      dispatch(resetReviewManageSidepanelState());
    },
    [dispatch]
  );

  return (
    <BulkActionContextProvider>
      <Box position="relative">
        <Box p={!checkNewNavEnabled() ? 4 : 0}>
          <Flex gap="16px">
            <Box w="calc(50% - 8px)" h="350px">
              <DisciplinaryActionEmployeeTrendChart
                dateRange={formattedReportDateRange}
                companyId={selectedCompanies?.map((company) => company.id)}
                location={selectedLocations}
              />
            </Box>
            <Box w="calc(50% - 8px)" h="350px">
              <DisciplinaryActionViolationCountChart
                dateRange={formattedReportDateRange}
                companyId={selectedCompanies?.map((company) => company.id)}
                location={selectedLocations}
              />
            </Box>
          </Flex>
          <Box my={3}>
            <AdminReportMetricCard
              data={adminReportsData}
              isLoading={isLoading}
              error={error}
              gridStatePersistenceKey={gridStatePersistenceKey}
            />
          </Box>
          <PaperWithShadow p={4} mb={1} position="relative">
            <MyCompanyAdminReportsGrid
              gridRef={gridRef}
              gridStatePersistenceKey={gridStatePersistenceKey}
              adminReportsData={adminReportsData}
            />
          </PaperWithShadow>
        </Box>
      </Box>
    </BulkActionContextProvider>
  );
}

export { MyCompanyAllAdminReportsIntent };
